import useManyRemoteM1 from '@aims/shared/shared/use-many-remote-m1';
import { PlusCircleOutlined } from '@ant-design/icons';
import { Button, Segmented, Select, Space, Tag } from 'antd';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import CSPage from '../../components/CSPage';
import CSPageHeader from '../../components/CSPageHeader';
import settings from '../../settings';
import {
  PartnerOnboardingStatuses,
  PartnerTypes,
  allPartnerOnboardingsQuery,
  extractAllPartnerOnboardings,
} from './constants';
import SelectRegionForAdmin from '../people_groups/ListPeopleGroups/SelectRegionForAdmin';
import ListPartnerOnboardings from './ListPartnerOnboardings';
import SelectCoordinator from './SelectCoordinator';
import KanbanView from './KanbanView';
import CreatePartnerOnboardingModal from './CreatePartnerOnboardingModal';

// Filters the dashboard can deep-link into, e.g.
// /partner-onboarding?status=IN_PROGRESS or ?assignedStaffId=<id>&staffName=<name>.
// Sentinel a deep link uses to mean "every status", distinct from "unspecified".
const ALL_STATUSES = 'ALL';

const URL_FILTER_KEYS = [
  'status',
  'region',
  'partnerType',
  'coordinatorContactId',
  'assignedStaffId',
];

function ListPartnerOnboardingsPage() {
  const location = useLocation();
  // Read once on mount: navigating here from the dashboard remounts the page.
  const initialFilters = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const found = {};
    URL_FILTER_KEYS.forEach((key) => {
      const value = params.get(key);
      if (value) found[key] = value;
    });
    // This page is for partners currently being onboarded -- completed rounds are read
    // on the contact's own onboarding tab. Staff can still clear or change the control.
    // `status=ALL` is how a caller counting every status (the dashboard tiles) asks for
    // the unfiltered list rather than inheriting this default.
    if (found.status === ALL_STATUSES) {
      delete found.status;
    } else if (!found.status) {
      found.status = PartnerOnboardingStatuses.IN_PROGRESS.key;
    }
    return found;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const initialStaffName = useMemo(
    () => new URLSearchParams(location.search).get('staffName'),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const [creating, setCreating] = useState(false);
  const [viewMode, setViewMode] = useState('table');
  const [status, setStatus] = useState(initialFilters.status);
  const [region, setRegion] = useState(initialFilters.region);
  const [partnerType, setPartnerType] = useState(initialFilters.partnerType);
  const [coordinatorContactId, setCoordinatorContactId] = useState(
    initialFilters.coordinatorContactId,
  );
  const [assignedStaffId, setAssignedStaffId] = useState(
    initialFilters.assignedStaffId,
  );
  const filters = useRef({ ...initialFilters });
  const sortBy = useRef([{ key: 'updatedAt', order: 'DESC' }]);

  const {
    error,
    loading,
    data: partnerOnboardings,
    hasNextPage,
    next,
    refetch,
  } = useManyRemoteM1({
    query: allPartnerOnboardingsQuery,
    extract: extractAllPartnerOnboardings,
    first: settings.querySize,
    filters: filters.current,
    sortBy: sortBy.current,
    fetchPolicy: 'network-only',
    queryId: 'listPartnerOnboardingsPage',
  });

  const onFiltersChanged = useCallback(
    (changed) => {
      filters.current = { ...filters.current, ...changed };
      refetch();
    },
    [refetch],
  );

  return (
    <>
      <CSPage containerStyle={{ maxWidth: 'unset' }} title="Partner Onboarding">
        <CSPageHeader
          title="Partner Onboarding"
          topActions={[
            <Button
              key="add"
              type="link"
              icon={<PlusCircleOutlined />}
              size="large"
              onClick={() => setCreating(true)}
            >
              Add Partner Onboarding
            </Button>,
          ]}
        />
        <Space style={{ marginBottom: 16 }} wrap>
          <Select
            allowClear
            placeholder="Filter by status"
            style={{ width: 180 }}
            value={status}
            onChange={(val) => {
              setStatus(val);
              onFiltersChanged({ status: val });
            }}
          >
            {Object.values(PartnerOnboardingStatuses).map((s) => (
              <Select.Option key={s.key} value={s.key}>
                {s.label}
              </Select.Option>
            ))}
          </Select>
          <SelectRegionForAdmin
            placeholder="Filter by region"
            style={{ width: 160 }}
            value={region}
            onChange={(val) => {
              setRegion(val);
              onFiltersChanged({ region: val });
            }}
          />
          <Select
            allowClear
            placeholder="Filter by partner type"
            style={{ width: 220 }}
            value={partnerType}
            onChange={(val) => {
              setPartnerType(val);
              onFiltersChanged({ partnerType: val });
            }}
          >
            {Object.entries(PartnerTypes).map(([code, label]) => (
              <Select.Option key={code} value={code}>
                {label}
              </Select.Option>
            ))}
          </Select>
          <SelectCoordinator
            allowClear
            placeholder="Filter by coordinator"
            style={{ width: 240 }}
            value={coordinatorContactId}
            onChange={(val) => {
              setCoordinatorContactId(val);
              onFiltersChanged({ coordinatorContactId: val });
            }}
          />
          {assignedStaffId && (
            <Tag
              closable
              onClose={() => {
                setAssignedStaffId(undefined);
                onFiltersChanged({ assignedStaffId: undefined });
              }}
            >
              {`Assigned staff: ${initialStaffName || assignedStaffId}`}
            </Tag>
          )}
          <Segmented
            options={['Table', 'Board']}
            value={viewMode === 'table' ? 'Table' : 'Board'}
            onChange={(v) => setViewMode(v === 'Table' ? 'table' : 'board')}
          />
        </Space>
        {viewMode === 'table' ? (
          <ListPartnerOnboardings
            loading={loading}
            error={error}
            partnerOnboardings={partnerOnboardings}
            next={next}
            hasNextPage={hasNextPage}
            refetch={refetch}
          />
        ) : (
          <KanbanView
            partnerOnboardings={partnerOnboardings}
            refetch={refetch}
            statusFilter={status}
          />
        )}
        <CreatePartnerOnboardingModal
          visible={creating}
          onCancel={() => setCreating(false)}
          onSuccess={() => {
            setCreating(false);
            refetch();
          }}
        />
      </CSPage>
    </>
  );
}

export default ListPartnerOnboardingsPage;
